import type {
  AuthTokens,
  BotDifficulty,
  ClockConfig,
  HandicapConfig,
  MatchSnapshotPayload,
  RoomInfo,
  Ruleset,
  Side,
  MatchHistoryItem,
  MatchmakingFindResponse,
  MatchReplayData,
  GameReviewReport,
  UserProfileStats,
  LeaderboardEntry,
  SocialPlayer,
  FriendItem,
  FriendRequestItem,
  SocialNotification,
  DirectMessage,
  ConversationItem,
} from '@xiangqi/protocol';
import { clearSession, loadSession, saveSession, type StoredSession } from './session.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

let session: StoredSession | null = loadSession();
let refreshing: Promise<void> | null = null;

const DEFAULT_REMOTE_HOST = 'https://play.kychien.vn';

export function getApiBaseUrl(): string {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (typeof location !== 'undefined') {
    if (
      location.hostname &&
      location.hostname !== 'localhost' &&
      !location.hostname.startsWith('127.') &&
      location.protocol.startsWith('http')
    ) {
      return '';
    }
  }
  return DEFAULT_REMOTE_HOST;
}

export function buildApiUrl(path: string): string {
  const base = getApiBaseUrl();
  return base ? `${base}${path}` : path;
}

export function getPublicWebUrl(path = ''): string {
  if (typeof location !== 'undefined') {
    if (
      location.hostname &&
      location.hostname !== 'localhost' &&
      !location.hostname.startsWith('127.') &&
      !location.hostname.includes('capacitor') &&
      location.protocol.startsWith('http')
    ) {
      return `${location.origin}${path}`;
    }
  }
  return `${DEFAULT_REMOTE_HOST}${path}`;
}

export function currentUser(): StoredSession['user'] | null {
  return session?.user ?? null;
}

async function rawRequest<T>(method: string, path: string, body?: unknown, retry = true, signal?: AbortSignal): Promise<T> {
  if (refreshing) {
    await refreshing;
  }
  const url = buildApiUrl(path);
  const token = session?.access_token;
  const res = await fetch(url, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });
  if (res.status === 401 && retry) {
    await refreshOnce();
    return rawRequest(method, path, body, false, signal);
  }
  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as T & { error?: { code: string; message: string } }) : ({} as T);
  if (!res.ok) {
    const err = (parsed as { error?: { code: string; message: string } }).error;
    throw new ApiError(res.status, err?.code ?? 'HTTP_ERROR', err?.message ?? `HTTP ${res.status}`);
  }
  return parsed;
}

async function refreshOnce(): Promise<void> {
  refreshing ??= (async () => {
    const current = session ?? loadSession();
    try {
      if (!current?.refresh_token) throw new Error('no refresh token');
      const res = await fetch(buildApiUrl('/v1/auth/refresh'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh_token: current.refresh_token }),
      });
      if (!res.ok) throw new Error('refresh failed');
      session = saveSession((await res.json()) as AuthTokens);
    } catch {
      clearSession();
      try {
        const guestTokens = await guestLogin();
        session = saveSession(guestTokens);
      } catch {
        session = null;
      }
    } finally {
      refreshing = null;
    }
  })();
  await refreshing;
}

async function guestLogin(): Promise<AuthTokens> {
  const res = await fetch(buildApiUrl('/v1/auth/guest'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new ApiError(res.status, 'GUEST_FAILED', 'không tạo được phiên khách');
  return (await res.json()) as AuthTokens;
}

let ensuring: Promise<StoredSession> | null = null;

/** Ensure there is SOME identity (guest by default) before any API call.
 * Single-flight: concurrent callers (e.g. React StrictMode double effects)
 * must not mint two guest accounts. */
export async function ensureSession(): Promise<StoredSession> {
  if (refreshing) await refreshing;
  if (session) return session;
  ensuring ??= (async () => {
    try {
      if (refreshing) await refreshing;
      if (session) return session;
      const tokens = await guestLogin();
      session = saveSession(tokens);
      return session;
    } finally {
      ensuring = null;
    }
  })();
  return ensuring;
}

export function adoptTokens(tokens: AuthTokens): void {
  session = saveSession(tokens);
}

// ---------------------------------------------------------------- endpoints

export const api = {
  register: (username: string, password: string, displayName?: string) =>
    rawRequest<AuthTokens>('POST', '/v1/auth/register', {
      username,
      password,
      ...(displayName ? { display_name: displayName } : {}),
    }),
  login: (username: string, password: string) =>
    rawRequest<AuthTokens>('POST', '/v1/auth/login', { username, password }),
  me: () => rawRequest<{ user: { user_id: string; display_name: string; username: string | null; kind: 'guest' | 'account' } | null }>('GET', '/v1/me'),
  createRoom: (
    ruleset: Ruleset,
    clock: ClockConfig,
    extra: {
      vs_bot?: { profile: BotDifficulty; human_side: Side | null };
      host_side?: Side | null;
      spectatable?: boolean;
      handicap?: HandicapConfig;
    } = {},
  ) => rawRequest<{ room: RoomInfo }>('POST', '/v1/rooms', { ruleset, clock, ...extra }),
  room: (code: string) => rawRequest<{ room: RoomInfo }>('GET', `/v1/rooms/${code}`),
  joinRoom: (code: string) => rawRequest<{ room: RoomInfo }>('POST', `/v1/rooms/${code}/join`),
  findMatch: (ruleset: Ruleset, clock?: ClockConfig, handicap?: HandicapConfig) =>
    rawRequest<MatchmakingFindResponse>('POST', '/v1/matchmaking/find', {
      ruleset,
      ...(clock ? { clock } : {}),
      ...(handicap ? { handicap } : {}),
    }),
  cancelMatchmaking: () => rawRequest<{ status: string }>('POST', '/v1/matchmaking/cancel', {}),
  matchSnapshot: (matchId: string) =>
    rawRequest<{ snapshot: MatchSnapshotPayload; stream_seq: number }>(
      'GET',
      `/v1/matches/${matchId}`,
    ),
  wsTicket: () => rawRequest<{ ticket: string }>('POST', '/v1/realtime/tickets', {}),
  history: (filter?: string) =>
    rawRequest<{ history: MatchHistoryItem[] }>(
      'GET',
      `/v1/users/me/history${filter ? `?filter=${encodeURIComponent(filter)}` : ''}`,
    ),
  replay: (matchId: string) =>
    rawRequest<{ replay: MatchReplayData }>('GET', `/v1/matches/${matchId}/replay`),
  review: (matchId: string) =>
    rawRequest<{ report: GameReviewReport }>('GET', `/v1/matches/${matchId}/review`),
  stats: () => rawRequest<{ stats: UserProfileStats }>('GET', '/v1/users/me/stats'),
  leaderboard: () => rawRequest<{ leaderboard: LeaderboardEntry[] }>('GET', '/v1/leaderboard'),
  socialSearch: (q: string) => rawRequest<{ players: SocialPlayer[] }>('GET', `/v1/social/search?q=${encodeURIComponent(q)}`),
  friends: () => rawRequest<{ friends: FriendItem[]; incoming: FriendRequestItem[]; outgoing: FriendRequestItem[] }>('GET', '/v1/social/friends'),
  sendFriendRequest: (target_user_id: string) => rawRequest<{ request_id: string }>('POST', '/v1/social/friend-requests', { target_user_id }),
  respondFriendRequest: (requestId: string, accept: boolean) => rawRequest<{ status: string }>('POST', `/v1/social/friend-requests/${requestId}/respond`, { accept }),
  cancelFriendRequest: (requestId: string) => rawRequest<{ status: string }>('DELETE', `/v1/social/friend-requests/${requestId}`),
  removeFriend: (userId: string) => rawRequest<{ status: string }>('DELETE', `/v1/social/friends/${userId}`),
  challengeFriend: (target_user_id: string, ruleset: Ruleset) => rawRequest<{ room: RoomInfo }>('POST', '/v1/social/challenges', { target_user_id, ruleset, clock: { initial_ms: 600_000, increment_ms: 5_000 } }),
  notifications: () => rawRequest<{ notifications: SocialNotification[]; unread_count: number }>('GET', '/v1/social/notifications'),
  markNotificationsRead: (notification_id?: string) => rawRequest<{ status: string }>('POST', '/v1/social/notifications/read', notification_id ? { notification_id } : {}),
  conversations: () => rawRequest<{ conversations: ConversationItem[] }>('GET', '/v1/social/conversations'),
  messages: (peerId: string) => rawRequest<{ messages: DirectMessage[] }>('GET', `/v1/social/messages/${peerId}`),
  sendMessage: (recipient_id: string, body: string) => rawRequest<{ message: DirectMessage }>('POST', '/v1/social/messages', { recipient_id, body }),
  deleteAccount: () => rawRequest<{ status: string }>('POST', '/v1/auth/delete-account'),
  getActiveBot: () =>
    rawRequest<{ activeBot: string; displayName: string }>('GET', '/v1/bot/active'),
  switchBot: (bot: string) =>
    rawRequest<{ activeBot: string; displayName: string }>('POST', '/v1/bot/switch', { bot }),
  getBotMove: (
    data: { ruleset?: string; turn: Side; publicState: unknown; history?: unknown[] },
    signal?: AbortSignal,
  ) =>
    rawRequest<{ move: { from: number; to: number }; score?: number; engine?: string }>(
      'POST',
      '/v1/bot/move',
      data,
      false,
      signal,
    ),
};
